import { useEffect, useMemo, useRef } from 'react';
import { VariableType } from '../../../types/dashboard.types';
import { ChartMode } from '../types';
import { useMarketAggregates } from '../../../contexts/MarketAggregatesContext';
import { convertToDeltas } from '../utils/dataProcessing';
import type { MarketZoneId } from '@app/common/static-data/energy-markets';

/**
 * Hook to access chart data from MarketAggregatesContext with mode-specific processing
 * This handles delta conversion locally without triggering API refetches
 */
export const useChartDataFromContext = (
  selectedVariable: VariableType,
  selectedZone: MarketZoneId | MarketZoneId[] | null,
  chartMode: ChartMode,
  selectedBaseline?: string | null,
  startDate?: string | null,
  endDate?: string | null,
) => {
  const { state, fetchChartData } = useMarketAggregates();
  // Normalize zone selection for stable fetch/caching
  const zonesSignature = useMemo(() => {
    if (Array.isArray(selectedZone)) {
      return [...new Set(selectedZone)].sort().join(',');
    }
    return selectedZone || '';
  }, [selectedZone]);
  const normalizedZones = useMemo(() => {
    if (Array.isArray(selectedZone)) {
      const norm = [...new Set(selectedZone)].sort() as MarketZoneId[];
      return norm.length === 0 ? null : (norm as any);
    }
    return selectedZone;
  }, [selectedZone]);

  // Keep a stable reference to fetchChartData to avoid re-running effect on identity changes
  const fetchRef = useRef(fetchChartData);
  useEffect(() => {
    fetchRef.current = fetchChartData;
  }, [fetchChartData]);

  // Trigger data fetch when dependencies change (excluding chartMode and selectedBaseline)
  // Include model selection state to ensure chart updates when selections are cleared
  const selectedModelsSignature = useMemo(() => {
    return state.availableModels
      .filter((model) => model.isSelected && model.selectedRuns.length > 0)
      .map(
        (model) =>
          `${model.id}:${model.selectedRuns.map((run) => `${run.date}-${run.time}`).join(',')}`,
      )
      .join('|');
  }, [state.availableModels]);

  useEffect(() => {
    fetchRef.current(selectedVariable, normalizedZones as any, chartMode, selectedBaseline);
    // Intentionally exclude chartMode and selectedBaseline to avoid refetching on UI-only changes
  }, [
    selectedVariable,
    zonesSignature,
    selectedModelsSignature,
    state.weightType,
    state.maxLeadTimeHours,
  ]);

  // Apply mode-specific processing and date filtering to the base data
  const processedChartData = useMemo(() => {
    let baseData = state.chartData.data;
    const availableBaselines = state.chartData.availableBaselines;

    // Apply date filtering first if dates are provided
    if ((startDate || endDate) && baseData.length > 0) {
      baseData = baseData.filter((point) => {
        const pointDate = new Date(point.time);
        const startDateObj = startDate ? new Date(startDate) : null;
        const endDateObj = endDate ? new Date(endDate) : null;

        if (startDateObj && pointDate < startDateObj) {
          return false;
        }
        if (endDateObj && pointDate > endDateObj) {
          return false;
        }
        return true;
      });
    }

    if (chartMode === 'delta' && baseData.length > 0 && availableBaselines.length > 1) {
      // Apply delta conversion locally without affecting cached base data
      return convertToDeltas(baseData, availableBaselines, selectedBaseline || undefined);
    }

    // For 'data' mode, return base data as-is (already includes statistical measures)
    return baseData;
  }, [
    state.chartData.data,
    state.chartData.availableBaselines,
    chartMode,
    selectedBaseline,
    startDate,
    endDate,
  ]);

  return {
    chartData: processedChartData,
    loading: state.chartData.loading,
    error: state.chartData.error,
    availableBaselines: state.chartData.availableBaselines,
  };
};
